import React, { useState, useEffect, useRef } from 'react';
import { Input, Button, Dropdown, Menu, Card, Empty } from 'antd';
import { SearchOutlined, MoreOutlined, LeftOutlined, DownOutlined, UpOutlined } from '@ant-design/icons';
import { useLazyQuery, useQuery } from '@apollo/client';
import { Get_Account_Experts, Get_Content_In_Other_Langauages, Get_Expert_Video_By_Id } from '../../../../../services/userAPI';
import './contentLibrary.css';
import { useLocation, useNavigate } from 'react-router-dom';
import Star from '../../../../../Images/starIcon.png';
import editIcon from '../../../../../Images/editIcon.png';
import TranslateIcon from '@mui/icons-material/Translate';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';

// VideoCard and formatDuration remain unchanged
const VideoCard = ({ video, expert, menuItems, activeDropdown, setActiveDropdown }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [showTooltip, setShowTooltip] = useState(false);
    const titleRef = useRef(null);
    const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });

    const isTruncated = video.title?.length > 60;
    const duration = formatDuration(video.videoLengthMins || 0, video.videoLengthSecs || 0);

    const handleTitleHover = () => {
        if (video.title?.length > 60 && titleRef.current) {
            const rect = titleRef.current.getBoundingClientRect();
            setTooltipPosition({
                top: rect.bottom + 5,
                left: rect.left,
            });
            setShowTooltip(true);
        }
    };

    const toggleExpansion = (e) => {
        e.stopPropagation();
        setIsExpanded(!isExpanded);
    };

    return (
        <div className={`video-card ${isExpanded ? 'video-card-expanded' : ''}`}>
            <div
                className='video-thumbnail'
                style={{
                    backgroundImage: `url(${video.vimeoThumbnailUrl || '/api/placeholder/320/180'})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                }}
            >
                <div className='video-duration'>{duration}</div>
                <div className='play-button'>
                    <div className='play-icon'></div>
                </div>
            </div>

            {video.originalContent === true ? (
                <div className='original-label'>
                    Original
                </div>
            ) : video.originalContent === false ? (
                <div className='original-placeholder'></div>
            ) : null}

            <div className='video-details'>
                <h3
                    ref={titleRef}
                    className='video-title'
                    onMouseEnter={handleTitleHover}
                    onMouseLeave={() => setShowTooltip(false)}
                >
                    {isTruncated ? video.title.substring(0, 60) + '...' : video.title}
                </h3>

                {showTooltip && (
                    <div
                        className='tooltip'
                        style={{
                            position: 'fixed',
                            top: `${tooltipPosition.top}px`,
                            left: `${tooltipPosition.left}px`,
                            backgroundColor: 'white',
                            border: '1px solid #ddd',
                            padding: '8px 12px',
                            borderRadius: '4px',
                            boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
                            zIndex: 1000,
                            maxWidth: '300px',
                            wordWrap: 'break-word',
                        }}
                    >
                        {video.title}
                    </div>
                )}

                <div className='author-row'>
                    <img
                        className='author-avatar'
                        src={Star}
                        alt={expert.name?.charAt(0) || '?'}
                        onError={(e) => {
                            e.target.onerror = null;
                            e.target.style.display = 'none';
                        }}
                    />
                    <span className='author-name'>{expert.name || 'Unknown Expert'}</span>
                    <Dropdown
                        overlay={menuItems(video.id)}
                        trigger={['click']}
                        visible={activeDropdown === video.id}
                        onVisibleChange={(visible) => {
                            setActiveDropdown(visible ? video.id : null);
                        }}
                    >
                        <Button
                            className='more-button'
                            icon={<MoreOutlined />}
                            onClick={(e) => e.stopPropagation()}
                        />
                    </Dropdown>
                </div>

                <div className='video-tags'>
                    <span className='language-tag' style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <TranslateIcon style={{ width: '12px', height: '12px' }} />
                        {video.language || 'N/A'}
                    </span>

                    <span className='category-tag' style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <CategoryOutlinedIcon style={{ width: '12px', height: '12px' }} />
                        {video.fourmatCategory || 'N/A'}
                    </span>
                </div>

                <p className='video-description'>
                    {isExpanded ? video.description : video.description?.substring(0, 55) + '...'}
                </p>

                <div className='view-more-container'>
                    <button type='link' className='view-more-button' onClick={toggleExpansion}>
                        {isExpanded ? 'View Less' : 'View More'}
                        <span className={`view-more-arrow ${isExpanded ? 'expanded' : ''}`}>
                            {isExpanded ? <UpOutlined /> : <DownOutlined />}
                        </span>
                    </button>
                </div>

                {isExpanded && (
                    <div className='metadata-dropdown'>
                        <div className='metadata-section'>
                            <div className='metadata-header'>Activity</div>
                            <div className='metadata-value'>{video.activityName || 'N/A'}</div>
                        </div>
                        <div className='metadata-section'>
                            <div className='metadata-header'>Task</div>
                            <div className='metadata-value'>{video.taskName || 'N/A'}</div>
                        </div>
                        <div className='metadata-section'>
                            <div className='metadata-header'>Sub-Task</div>
                            <div className='metadata-value'>{video.subtaskName || 'N/A'}</div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

function formatDuration(mins, secs) {
    const totalSeconds = mins * 60 + secs;
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
}

const ContentLibraryAll = () => {
    const location = useLocation();
    const navigate = useNavigate();

    // Extract expertId from navigation state
    const { expertId, accountId, roleId, accountName, name } = location.state || {};

    const [expertPagination, setExpertPagination] = useState({});
    const [activeDropdown, setActiveDropdown] = useState(null);
    const [searchText, setSearchText] = useState('');
    const [experts, setExperts] = useState([]);
    const [expertVideos, setExpertVideos] = useState({});
    const [selectedExpertId, setSelectedExpertId] = useState(expertId || null);
    const [cursor, setCursor] = useState(null);
    const limit = 10;
    const sortByField = 'created_at';
    const sortOrder = 'desc';
    const defaultPagingCriteria = {
        limit: 10,
        cursor: null,
        sortByField: 'created_at',
        sortOrder: 'desc',
    };

    const loadMoreRef = useRef(null);

    const {
        loading: expertsLoading,
        error: expertsError,
        data: expertsData,
    } = useQuery(Get_Account_Experts, {
        variables: {
            accountId,
            pagingCriteria: defaultPagingCriteria,
        },
        fetchPolicy: 'network-only',
    });

    const {
        loading: videosLoading,
        error: videosError,
        data: videosData,
        refetch: refetchVideos,
    } = useQuery(Get_Expert_Video_By_Id, {
        variables: {
            pagingCriteria: {
                limit,
                cursor,
                sortByField,
                sortOrder,
            },
            accountId: accountId,
            userId: selectedExpertId,
        },
        skip: !selectedExpertId,
        fetchPolicy: 'network-only',
    });

    const [getContentInOtherLanguages, { loading: otherLanguagesLoading, error: otherLanguagesError }] = useLazyQuery(Get_Content_In_Other_Langauages, {
        fetchPolicy: 'network-only',
    });

    useEffect(() => {
        if (expertsData && expertsData.getAccountExperts) {
            setExperts(expertsData.getAccountExperts.data);
            if (expertId && !selectedExpertId) {
                setSelectedExpertId(expertId);
            }
        }
    }, [expertsData, expertId, selectedExpertId]);

    useEffect(() => {
        if (videosData && videosData.getUserAllContributions && selectedExpertId) {
            const newVideos = videosData.getUserAllContributions.data || [];
            const pageMeta = videosData.getUserAllContributions.pageMeta || {};


            setExpertVideos((prev) => ({
                ...prev,
                [selectedExpertId]: cursor ? [...(prev[selectedExpertId] || []), ...newVideos] : newVideos,
            }));

            setExpertPagination((prev) => ({
                ...prev,
                [selectedExpertId]: {
                    nextCursor: pageMeta.nextPage,
                    prevCursor: pageMeta.previousPage,
                    hasNextPage: pageMeta.hasNextPage,
                    hasPreviousPage: pageMeta.hasPreviousPage,
                    loading: false,
                },
            }));
        }
    }, [videosData, selectedExpertId, cursor]);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                const firstEntry = entries[0];
                if (
                    firstEntry.isIntersecting &&
                    selectedExpertId &&
                    expertPagination[selectedExpertId]?.hasNextPage &&
                    !expertPagination[selectedExpertId]?.loading
                ) {
                    setExpertPagination((prev) => ({
                        ...prev,
                        [selectedExpertId]: {
                            ...prev[selectedExpertId],
                            loading: true,
                        },
                    }));
                    const nextCursor = expertPagination[selectedExpertId]?.nextCursor;
                    setCursor(nextCursor);
                }
            },
            { threshold: 0.1 }
        );

        if (loadMoreRef.current) {
            observer.observe(loadMoreRef.current);
        }

        return () => {
            if (loadMoreRef.current) {
                observer.unobserve(loadMoreRef.current);
            }
        };
    }, [selectedExpertId, expertPagination]);

    const handleMenuClick = async (e, videoId) => {
        setActiveDropdown(null);
        try {
            let expert = null;
            let expertId = null;
            let videos = null;
            let clickedVideo = null;

            // Find the expert and the clicked video
            for (const [expId, expVideos] of Object.entries(expertVideos)) {
                const foundVideo = expVideos.find(v => String(v.id) === String(videoId));
                if (foundVideo) {
                    expertId = expId;
                    videos = expVideos;
                    expert = experts.find(ex => String(ex.id) === String(expId));
                    clickedVideo = foundVideo;
                    break;
                }
            }

            if (!videos || !expert || !clickedVideo) {
                console.error('Videos or expert not found');
                navigate('/add-content-library', {
                    state: {
                        isEdit: true,
                        editData: {
                            videoId,
                            accountId,
                            roleId,
                            accountName,
                            name,
                            otherLanguagesContent: []
                        },
                        accountId,
                        roleId,
                        accountName,
                        name
                    }
                });
                return;
            }

            // Process quiz details for all videos under this expert (needed for stars array)
            const stars = videos.map(video => {
                let quizQuestion = '';
                let quizOptionA = '';
                let quizOptionB = '';
                let correctAnswer = '';

                if (video.quizDetails && video.quizDetails.answer && video.quizDetails.answer.length >= 2) {
                    quizQuestion = video.quizDetails.question || '';

                    const sortedAnswers = [...video.quizDetails.answer].sort((a, b) => a.id - b.id);

                    quizOptionA = sortedAnswers[0]?.optionsAnswer || '';
                    quizOptionB = sortedAnswers[1]?.optionsAnswer || '';

                    const correctAnswerObj = video.quizDetails.answer.find(ans => ans.isanswer === true);
                    if (correctAnswerObj) {
                        if (correctAnswerObj.id === sortedAnswers[0].id) {
                            correctAnswer = 'A';
                        } else if (correctAnswerObj.id === sortedAnswers[1].id) {
                            correctAnswer = 'B';
                        } else {
                            correctAnswer = correctAnswerObj.optionsAnswer === quizOptionA ? 'A' : 'B';
                        }
                    }
                }

                return {
                    videoId: video.id,
                    accountId,
                    roleId,
                    userId: expertId,
                    name: expert.name,
                    employeeId: expert.employeeId || 'No ID',
                    youtubeCode: video.code || '',
                    videoTitle: video.title || '',
                    videoThumbnailCode: video.vimeoThumbnailUrl || '',
                    duration: formatDuration(video.videoLengthMins || 0, video.videoLengthSecs || 0),
                    videoLanguage: video.languageId || video.language || '',
                    originalContent: video.originalContent || false,
                    videoDescription: video.description || '',
                    keyPoints: video.keyPoints?.length ? video.keyPoints.map(kp => ({
                        id: kp.id,
                        keyPoint: kp.keyPoint,
                        contentId: kp.contentId
                    })) : [],
                    quizQuestion: quizQuestion,
                    quizOptionA: quizOptionA,
                    quizOptionB: quizOptionB,
                    correctAnswer: correctAnswer,
                    activityId: video.activityId || '',
                    activityName: video.activityName || '',
                    taskId: video.taskId || '',
                    taskName: video.taskName || '',
                    subTaskId: video.subtaskId || '',
                    subTaskName: video.subtaskName || '',
                    format: video.fourmatCategory || '',
                    commitmentNeeded: video.commitmentNeeded || false,
                    contentId: parseInt(video.id) || 0
                };
            });

            // Sort stars to prioritize the selected video
            const sortedStars = [
                stars.find(s => String(s.videoId) === String(videoId)),
                ...stars.filter(s => String(s.videoId) !== String(videoId))
            ].filter(Boolean);

            // Fetch other languages content only for the clicked video
            let otherLanguagesContent = [];
            try {
                const { data: otherLanguagesData } = await getContentInOtherLanguages({
                    variables: { contentId: parseInt(videoId) }
                });
                const relatedContent = (otherLanguagesData?.getContentInOtherLangauages || []).map(item => ({
                    ...item,
                    videoId: item.id // Use the item's own id as videoId
                }));
                otherLanguagesContent = [...otherLanguagesContent, ...relatedContent];
            } catch (langError) {
                console.error(`Error fetching other languages for video ${videoId}:`, langError);
            }

            // If no other languages content found, include the clicked video itself
            const videoIdsWithOtherContent = new Set(otherLanguagesContent.map(item => String(item.videoId)));
            if (!videoIdsWithOtherContent.has(String(videoId))) {
                otherLanguagesContent.push({
                    ...clickedVideo,
                    videoId: clickedVideo.id
                });
            }

            // Navigate to edit page with the filtered data
            navigate('/add-content-library', {
                state: {
                    isEdit: true,
                    editData: {
                        stars: sortedStars, // Still pass all stars for quiz data lookup
                        accountId,
                        roleId,
                        accountName,
                        name,
                        otherLanguagesContent
                    },
                    accountId,
                    roleId,
                    accountName,
                    name
                }
            });

        } catch (error) {
            console.error('Error in handleMenuClick:', error);
            navigate('/add-content-library', {
                state: {
                    isEdit: true,
                    editData: {
                        videoId,
                        accountId,
                        roleId,
                        accountName,
                        name,
                        otherLanguagesContent: []
                    },
                    accountId,
                    roleId,
                    accountName,
                    name
                }
            });
        }
    };

    const menuItems = (videoId) => (
        <Menu onClick={(e) => handleMenuClick(e, videoId)}>
            <Menu.Item key='edit' icon={<img src={editIcon} alt="" style={{ width: 14, height: 14, objectFit: 'contain' }} />}>Edit</Menu.Item>
            <Menu.Item key='delete'>Delete</Menu.Item>
            <Menu.Item key='share'>Share</Menu.Item>
        </Menu>
    );

    const filterVideosBySearch = (videos) => {
        if (!searchText.trim()) return videos;

        return videos.filter(
            (video) =>
                video.title?.toLowerCase().includes(searchText.toLowerCase()) ||
                video.description?.toLowerCase().includes(searchText.toLowerCase()) ||
                video.language?.toLowerCase().includes(searchText.toLowerCase()) ||
                video.fourmatCategory?.toLowerCase().includes(searchText.toLowerCase())
        );
    };

    // Render videos only for the selected expert
    const renderExpertContentSections = () => {
        const selectedExpert = experts.find((expert) => expert.id === selectedExpertId);
        if (!selectedExpert) {
            return <Empty description="Selected expert not found" />;
        }

        const expertVideosData = expertVideos[selectedExpertId] || [];
        const filteredVideos = filterVideosBySearch(expertVideosData);
        const isLoadingMore = expertPagination[selectedExpertId]?.loading;
        const hasMore = expertPagination[selectedExpertId]?.hasNextPage;

        return (
            <div className='content-section' key={selectedExpertId}>
                <div className='video-grid'>
                    {filteredVideos.length > 0 ? (
                        filteredVideos.map((video) => (
                            <VideoCard
                                key={video.id}
                                video={video}
                                expert={selectedExpert}
                                menuItems={menuItems}
                                activeDropdown={activeDropdown}
                                setActiveDropdown={setActiveDropdown}
                            />
                        ))
                    ) : (
                        <Empty description={`No videos found for ${selectedExpert.name}`} />
                    )}
                </div>
                <div
                    ref={loadMoreRef}
                    style={{ height: '20px', textAlign: 'center' }}
                >
                    {isLoadingMore && (
                        <div className='loading-container'>
                            <div className='spinner'></div>
                            <p>Loading more videos...</p>
                        </div>
                    )}
                    {/* {!isLoadingMore && !hasMore && filteredVideos.length > 0 && (
                        <p>No more videos to load</p>
                    )} */}
                </div>
            </div>
        );
    };

    return (
        <div className="user-profile-container content-library-more-videos" style={{ maxHeight: "100vh" }}>
            <h2 onClick={() => navigate(-1)} className='section-title-contentLibrary'>
                <LeftOutlined style={{ width: '12px', height: '12px', marginRight: '0.5%', cursor: 'pointer' }} />
                More Videos from {experts.find((expert) => expert.id === selectedExpertId)?.name || 'Expert'}
            </h2>
            <div className='search-add-container'>
                <div className='search-container-contentLibrary'>
                    <Input
                        placeholder={`Search ${experts.find((expert) => expert.id === selectedExpertId)?.name || 'expert'} content`}
                        prefix={<SearchOutlined style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)' }} />}
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        className='search-input'
                    />
                </div>
                <button
                    className='add-content-button'
                    onClick={() =>
                        navigate('/add-content-library', {
                            state: { roleId: roleId, accountId: accountId, accountName: accountName, name: name },
                        })
                    }
                >
                    Add Content
                </button>
            </div>

            <Card className="content-library-video-list">
                {expertsLoading ? (
                    <div className='loading-container' style={{ textAlign: 'center', padding: '16px' }}>
                        <div className='spinner'></div>
                        <p>Loading experts...</p>
                    </div>
                ) : expertsError ? (
                    <div className='error-container'>
                        <p>Error loading experts. Please try again later.</p>
                    </div>
                ) : selectedExpertId && experts.length > 0 ? (
                    renderExpertContentSections()
                ) : (
                    <Empty description='No experts found' />
                )}
            </Card>
        </div>
    );
};

export default ContentLibraryAll;